import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart3, 
  AlertTriangle, 
  TrendingUp,
  MapPin,
  Users,
  Zap
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RiskAssessment = () => {
  const { language } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const [summaryRes, policiesRes] = await Promise.all([
        axios.get(`${API}/risk-scores/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/policies?limit=20`)
      ]);
      setSummary(summaryRes.data);
      setPolicies(policiesRes.data);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar datos' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#D9534F', '#FFC107', '#4CAF50'];

  const pieData = summary ? [
    { name: language === 'es' ? 'Alto' : 'High', value: summary.high_risk_count, color: '#D9534F' },
    { name: language === 'es' ? 'Medio' : 'Medium', value: summary.medium_risk_count, color: '#FFC107' },
    { name: language === 'es' ? 'Bajo' : 'Low', value: summary.low_risk_count, color: '#4CAF50' }
  ] : [];

  const barData = policies.slice(0, 6).map(p => ({
    name: p.title.length > 20 ? p.title.substring(0, 20) + '...' : p.title,
    score: p.risk_score,
    fullName: p.title
  }));

  if (loading) {
    return (
      <div className="space-y-6" data-testid="risk-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="risk-assessment-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {language === 'es' ? 'Evaluación de Riesgos' : 'Risk Assessment'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'es' 
            ? 'Análisis de impacto comunitario y puntuación de riesgos'
            : 'Community impact analysis and risk scoring'}
        </p>
      </div>

      {/* Risk Methodology Explanation */}
      <Card className="border-primary/20 bg-primary/5" data-testid="risk-methodology">
        <CardHeader>
          <CardTitle className="text-lg">
            {language === 'es' ? '¿Cómo Calculamos el Riesgo?' : 'How We Calculate Risk'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-accent" />
                <span className="font-semibold text-accent">
                  {language === 'es' ? 'Alto Riesgo (70-100)' : 'High Risk (70-100)'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Políticas que podrían aumentar directamente la contaminación, eliminar protecciones, o afectar desproporcionadamente a comunidades de justicia ambiental. Se requiere acción inmediata.'
                  : 'Policies that could directly increase pollution, remove protections, or disproportionately harm environmental justice communities. Immediate action needed.'}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-warning/10 border border-warning/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-warning" />
                <span className="font-semibold text-warning">
                  {language === 'es' ? 'Riesgo Medio (40-69)' : 'Medium Risk (40-69)'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Políticas con impacto incierto o mixto. Pueden tener efectos positivos y negativos, o el resultado depende de la implementación. Monitorear de cerca.'
                  : 'Policies with uncertain or mixed impact. May have both positive and negative effects, or outcome depends on implementation. Monitor closely.'}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-success/10 border border-success/20">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-5 w-5 text-success" />
                <span className="font-semibold text-success">
                  {language === 'es' ? 'Bajo Riesgo (0-39)' : 'Low Risk (0-39)'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Políticas beneficiosas que fortalecen protecciones, dirigen inversiones a comunidades EJ, o abordan injusticias históricas. El puntaje refleja confianza en resultados positivos.'
                  : 'Beneficial policies that strengthen protections, direct investment to EJ communities, or address historical injustices. Score reflects confidence in positive outcomes.'}
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4 pt-4 border-t border-border">
            {language === 'es' 
              ? 'Nuestras puntuaciones de riesgo consideran: proximidad a poblaciones vulnerables, carga de contaminación existente, historial de violaciones de instalaciones, indicadores de vulnerabilidad demográfica, y patrones históricos de redlining.'
              : 'Our risk scores consider: proximity to vulnerable populations, existing pollution burden, facility violation history, demographic vulnerability indicators, and historical redlining patterns.'}
          </p>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="stat-avg-risk">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Riesgo Promedio' : 'Average Risk'}
                </p>
                <p className="text-3xl font-bold mt-1">{summary?.average_risk_score || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-high">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Alto Riesgo' : 'High Risk'}
                </p>
                <p className="text-3xl font-bold mt-1 text-accent">{summary?.high_risk_count || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-medium">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Riesgo Medio' : 'Medium Risk'}
                </p>
                <p className="text-3xl font-bold mt-1 text-warning">{summary?.medium_risk_count || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stat-low">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {language === 'es' ? 'Bajo Riesgo' : 'Low Risk'}
                </p>
                <p className="text-3xl font-bold mt-1 text-success">{summary?.low_risk_count || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <Zap className="h-6 w-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Risk Distribution Pie Chart */}
        <Card data-testid="risk-distribution-chart">
          <CardHeader>
            <CardTitle>
              {language === 'es' ? 'Distribución de Riesgos' : 'Risk Distribution'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {pieData.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Policy Risk Scores Bar Chart */}
        <Card data-testid="policy-risk-chart">
          <CardHeader>
            <CardTitle>
              {language === 'es' ? 'Puntuaciones por Política' : 'Policy Risk Scores'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                            <p className="font-medium text-sm">{payload[0].payload.fullName}</p>
                            <p className="text-muted-foreground text-sm">
                              {language === 'es' ? 'Puntuación' : 'Score'}: {payload[0].value}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar 
                    dataKey="score" 
                    fill="hsl(var(--primary))"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Factors */}
      <Card data-testid="risk-factors">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            {language === 'es' ? 'Factores de Riesgo Comunitario' : 'Community Risk Factors'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Proximidad a Industrias' : 'Industrial Proximity'}
                  </span>
                  <span className="text-sm text-muted-foreground">72%</span>
                </div>
                <Progress value={72} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Carga de Contaminación' : 'Pollution Burden'}
                  </span>
                  <span className="text-sm text-muted-foreground">65%</span>
                </div>
                <Progress value={65} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Indicadores de Salud' : 'Health Indicators'}
                  </span>
                  <span className="text-sm text-muted-foreground">58%</span>
                </div>
                <Progress value={58} className="h-2" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Vulnerabilidad Demográfica' : 'Demographic Vulnerability'}
                  </span>
                  <span className="text-sm text-muted-foreground">45%</span>
                </div>
                <Progress value={45} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Historial de Redlining' : 'Redlining History'}
                  </span>
                  <span className="text-sm text-muted-foreground">80%</span>
                </div>
                <Progress value={80} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {language === 'es' ? 'Historial de Violaciones' : 'Violation History'}
                  </span>
                  <span className="text-sm text-muted-foreground">35%</span>
                </div>
                <Progress value={35} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* High Risk Policies List */}
      <Card data-testid="high-risk-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-accent" />
            {language === 'es' ? 'Políticas de Alto Riesgo' : 'High Risk Policies'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {policies
              .filter(p => p.risk_score >= 70)
              .slice(0, 5)
              .map((policy, index) => (
                <div 
                  key={policy.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/60 hover:border-accent/30 transition-colors"
                  data-testid={`high-risk-policy-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-8 rounded-full bg-accent" />
                    <div>
                      <p className="font-medium text-sm">{policy.title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {policy.jurisdiction} {policy.state && `- ${policy.state}`}
                      </p>
                    </div>
                  </div>
                  <Badge className="risk-high">{policy.risk_score}</Badge>
                </div>
              ))}
            {policies.filter(p => p.risk_score >= 70).length === 0 && (
              <p className="text-center text-muted-foreground py-4">
                {language === 'es' 
                  ? 'No hay políticas de alto riesgo actualmente'
                  : 'No high risk policies currently'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RiskAssessment;
